import {
  Button,
  Form,
  FormInstance,
  Space,
  Alert,
  message,
  Typography,
  Row,
  Col,
} from "antd";
import { ReactNode, useEffect, useMemo, useState } from "react";
import { DynamicSchema, FormAction } from "./DynamicForm.default";
import { http } from "../Guard";
import { getProblemMessage } from "../utils/http";
import _ from "lodash";
import DynamicForm from "./DynamicForm";
import RelatedEntityManager from "./RelatedEntityManager";
import { InputObject } from "./default";

export interface DynamicFormActionsProps<
  T extends Record<string, any>,
  R = any,
> {
  // Core form properties
  readonly mode: FormAction;
  readonly initialValues?: T;
  readonly onFinish?: () => void;
  readonly onCancel?: () => void;

  // Schema can be provided directly or through createSchema
  readonly schema?: DynamicSchema;
  readonly createSchema?: (values: T) => DynamicSchema;

  // API calls with common pattern - these will be used only if no URL is provided in the schema
  readonly apiCalls?: {
    add?: (values: T) => Promise<R>;
    update?: (values: T) => Promise<R>;
    delete?: (id: string) => Promise<void>;
    get?: (id: string) => Promise<T>; // For fetching entity
  };

  // HTTP method override (optional - will default based on mode)
  readonly methodOverride?: {
    add?: "POST" | "PUT";
    update?: "PATCH" | "PUT";
    delete?: "DELETE";
  };

  // Custom validation logic
  readonly validate?: (values: T) => boolean;

  // Form component props to pass to DynamicForm
  readonly className?: string;
  readonly disabled?: boolean;
  readonly preview?: boolean;
  readonly onChange?: (values: any) => void;

  // UI configuration
  readonly showActions?: boolean;
  readonly showErrors?: boolean;
  readonly actionButtons?: ReactNode;
  readonly confirmButtonText?: string;
  readonly cancelButtonText?: string;
  readonly errorText?: string;
  readonly fetchErrorText?: string;
  readonly fetchData?: boolean; // Control whether to fetch data automatically

  // New property for related entities UI
  readonly relatedEntitiesUI?: {
    [entityName: string]: {
      title: string;
      addLabel?: string;
      emptyText?: string;
      request: (search: string) => Promise<InputObject[]>;
      displayField?: string;
      url: string;
      onAdd?: (item: InputObject) => void;
      onRemove?: (id: string) => void;
    };
  };
}

/**
 * A component that encapsulates form logic and provides a clean API for form handling.
 * It renders a DynamicForm directly with form actions.
 */
function DynamicFormActions<T extends Record<string, any>, R = any>(
  props: DynamicFormActionsProps<T, R>,
) {
  // Form and state
  const [form] = Form.useForm<T>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [valid, setValid] = useState(false);
  const [messageApi] = message.useMessage();
  const [isSubmitted, setIsSubmitted] = useState(false);

  const [parentUid, setParentUid] = useState<string | undefined>(undefined);

  // Destructure props to avoid dependency issues
  const {
    schema,
    createSchema,
    validate,
    mode,
    initialValues,
    fetchData,
    showActions = true,
    showErrors = true,
  } = props;

  // Get form values
  const watchResult = Form.useWatch([], form);
  const values = useMemo(() => {
    return (watchResult as T) || ({} as T);
  }, [watchResult]);

  // Generate schema based on values or use provided schema
  const apiSchema = useMemo(() => {
    if (schema) {
      return schema;
    } else if (createSchema && values) {
      return createSchema(values);
    }
    return undefined;
  }, [values, createSchema, schema]);

  // Clean schema for DynamicForm by removing API-specific properties
  const formSchema = useMemo(() => {
    if (!apiSchema) return undefined;

    // Create a clean copy without API-specific properties
    return {
      ...apiSchema,
      // Remove API-specific properties that DynamicForm doesn't need
      url: undefined,
      idField: undefined,
      supportedActions: undefined,
      relatedEntities: undefined,
    };
  }, [apiSchema]);

  // Fetch data when component mounts or initialValues changes
  useEffect(() => {
    if (
      (mode === "update" || mode === "view") &&
      initialValues?.uid &&
      fetchData !== false
    ) {
      setParentUid(initialValues.uid);
      // Fetch data function
      const fetchEntityData = async () => {
        // Get the ID from initialValues
        const entityId = initialValues?.uid;
        if (!entityId) return;

        setLoading(true);
        setError(false);

        try {
          let mainData: T | undefined;

          // Fetch main entity data
          if (apiSchema?.url) {
            const result = await http.get(`${apiSchema.url}/${entityId}`);
            mainData = result.data;
          } else if (props.apiCalls?.get) {
            mainData = await props.apiCalls.get(entityId);
          }

          if (!mainData) {
            throw new Error("Failed to fetch data");
          }

          setFormFields(form, mainData);
          setLoading(false);
        } catch (error) {
          console.error("Error fetching data:", error);
          setLoading(false);
          setError(true);
          messageApi.error(props.fetchErrorText || "Failed to load data");
        }
      };
      fetchEntityData();
    } else if (initialValues) {
      setParentUid(undefined);
      setFormFields(form, initialValues);
    }
  }, [initialValues?.uid, mode, fetchData]);

  // Custom validation
  useEffect(() => {
    if (!values || Object.keys(values).length === 0) {
      setValid(false);
      return;
    }

    // If there's a custom validation function, use it
    if (validate) {
      setValid(validate(values));
    }
    // Otherwise, simple validation based on if form has values
    else {
      const hasValues = Object.values(values).some(
        (val) => val !== undefined && val !== null && val !== "",
      );

      // For add/update modes, require values
      // For delete mode, valid by default if we have initialValues
      if (mode === "delete") {
        setValid(!!initialValues);
      } else {
        setValid(hasValues);
      }
    }
  }, [values, validate, mode, initialValues]);

  // Check if the current mode is supported
  const isModeSupported = useMemo(() => {
    if (!apiSchema?.supportedActions) return true;
    return apiSchema.supportedActions.includes(mode);
  }, [apiSchema, mode]);

  // Helper function to get the HTTP method based on the mode
  const getHttpMethod = (mode: FormAction): string => {
    if (mode === "add") {
      return props.methodOverride?.add || "POST";
    } else if (mode === "update") {
      return props.methodOverride?.update || "PATCH";
    } else if (mode === "delete") {
      return props.methodOverride?.delete || "DELETE";
    }
    return "GET";
  };

  // Handle submission
  const onSubmit = async (): Promise<void> => {
    try {
      // Check if the mode is supported
      if (!isModeSupported) {
        messageApi.error(`The ${mode} action is not supported for this form.`);
        return;
      }

      // Validate form first
      const formValues = await form.validateFields();

      setLoading(true);
      setError(false);

      // If a URL is provided in the schema, use it for API calls
      if (apiSchema?.url) {
        const idField = apiSchema.idField || "uid";
        const method = getHttpMethod(mode);
        let result;

        try {
          if (mode === "add") {
            // Create new entity
            result = await http[method.toLowerCase() as "post" | "put"](
              apiSchema.url,
              formValues,
            );
            setParentUid(result.data.uid);
            setIsSubmitted(true);
          } else if (mode === "update") {
            const id = initialValues?.[idField];

            // Get only touched fields
            const touchedFields = form.getFieldsValue(
              true,
              (meta) => meta.touched,
            );

            // Check if any fields were actually touched
            if (Object.keys(touchedFields).length === 0) {
              // No fields were touched, so we can skip the update
              props.onFinish?.();
              setLoading(false);
              setIsSubmitted(true);
              return;
            }

            // Update existing entity with rowVersion for optimistic concurrency control
            const updateData = {
              ..._.omit(touchedFields, [idField]),
              // Include rowVersion if it exists in initialValues
              ...(initialValues?.rowVersion && {
                rowVersion: initialValues.rowVersion,
              }),
            };

            await http[method.toLowerCase() as "patch" | "put"](
              `${apiSchema.url}/${id}`,
              updateData,
            );
            setIsSubmitted(true);
          } else if (mode === "delete") {
            const id = initialValues?.[idField];

            // Delete entity
            await http[method.toLowerCase() as "delete"](
              `${apiSchema.url}/${id}`,
            );
          }

          // Call onFinish when done
          props.onFinish?.();
        } catch (apiError: any) {
          console.error("API error:", apiError);
          messageApi.error(
            getProblemMessage(apiError, "An error occurred during the operation"),
          );
          setError(true);
          setParentUid(undefined);
          setIsSubmitted(false);
        }
      } else {
        messageApi.error(
          "Form submission failed. No API endpoint or handler provided.",
        );
        setError(true);
        setParentUid(undefined);
      }

      setLoading(false);
    } catch (error) {
      console.error("Form submission error:", error);
      setLoading(false);
      setError(true);
    }
  };

  // Cancel form
  const onCancel = (): void => {
    props.onCancel?.();
  };

  //   // Reset form
  //   const reset = (): void => {
  //     form.resetFields();
  //     setValid(false);
  //     setError(false);
  //   };

  // Set initial values when they change
  useEffect(() => {
    if (initialValues) {
      form.setFieldsValue(initialValues);
    } else {
      form.resetFields();
    }
  }, [initialValues, form]);

  // Delete mode rendering - just show a confirmation message
  if (mode === "delete") {
    return (
      <div className="flex flex-col gap-4">
        <Typography.Paragraph>
          Are you sure you want to delete this item?
        </Typography.Paragraph>

        {error && showErrors !== false && (
          <Alert
            className="mb-2"
            message={props.errorText || "Something went wrong, try again"}
            type="error"
          />
        )}

        {showActions !== false ? (
          <div className="flex justify-end">
            <Space>
              <Button disabled={loading} onClick={onCancel}>
                {props.cancelButtonText || "Cancel"}
              </Button>
              <Button
                type="primary"
                danger
                loading={loading}
                onClick={onSubmit}
              >
                {props.confirmButtonText || "Delete"}
              </Button>
            </Space>
          </div>
        ) : null}
      </div>
    );
  }

  // Render the form with actions
  return (
    <>
      <Row className="mb-4 flex flex-row">
        {/* Main form */}
        <Col className="flex flex-1 flex-col">
          <DynamicForm
            form={form}
            schema={formSchema}
            disabled={
              props.disabled ||
              loading ||
              props.mode === "view" ||
              props.preview
            }
            preview={props.preview}
            onChange={props.onChange}
          />
        </Col>

        {/* Related entities UI */}
        {formSchema?.relatedEntitiesUI &&
          Object.keys(formSchema.relatedEntitiesUI).length > 0 && (
            <>
              {Object.entries(formSchema.relatedEntitiesUI).map(
                ([entityName, config]) => {
                  return (
                    <RelatedEntityManager
                      isSubmitted={isSubmitted}
                      setIsSubmitted={setIsSubmitted}
                      entityConfiguration={config.entityConfiguration}
                      needsOrderToAdd={config?.needsOrderToAdd}
                      key={entityName}
                      title={config.title}
                      addLabel={config.addLabel}
                      emptyText={config.emptyText}
                      getUrl={config.getUrl}
                      deleteUrl={config.deleteUrl}
                      addUrl={config.addUrl}
                      request={config.request}
                      parentUid={parentUid}
                      displayField={config.displayField}
                      mode={props.mode}
                    />
                  );
                },
              )}
            </>
          )}
      </Row>

      {/* Error message */}
      {showErrors !== false && error && (
        <Alert
          className="mb-2 mt-4"
          message={props.errorText || "An error occurred"}
          type="error"
        />
      )}

      {/* Form actions */}
      {showActions !== false && (
        <div className="mt-4 flex justify-end">
          {props.actionButtons || (
            <Space>
              <Button onClick={onCancel} disabled={loading}>
                {props.cancelButtonText || "Cancel"}
              </Button>
              {props.mode !== "view" && (
                <Button
                  type="primary"
                  onClick={onSubmit}
                  loading={loading}
                  disabled={!valid && props.mode !== "delete"}
                  danger={props.mode === "delete"}
                >
                  {props.confirmButtonText ||
                    (props.mode === "add"
                      ? "Add"
                      : props.mode === "update"
                        ? "Save"
                        : props.mode === "delete"
                          ? "Delete"
                          : "Submit")}
                </Button>
              )}
            </Space>
          )}
        </div>
      )}
    </>
  );
}

export function setFormFields<T extends object>(
  form: FormInstance,
  initialValues: T,
) {
  form.setFields(
    Object.entries(initialValues).map(([name, value]) => ({
      name,
      value,
      touched: false,
    })),
  );
}

export default DynamicFormActions;
